import { useCallback, useEffect, useRef, useState } from "react";
import type {
  TransferTreatmentStartView,
  TreatmentStartPrecision,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchTransferTreatmentStart,
  setTransferTreatmentStart,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { useReadOnly } from "./SupportView";

// TES-1398 (Ö-420, Ö-421; Emelie 2026-10-15) — an approved prescription
// transfer's treatment start on the patient overview: the date, whether it
// is exact or estimated, and what it gives her today (treatment day and DT).
// "Startdatumet ... verifieras eller korrigeras av läkaren", and "Om det
// exakta startdatumet inte kan verifieras ska läkaren ange ett operativt
// uppskattat startdatum och precisionen markeras som uppskattad."
//
// The approval records the start first (TransferReviewPanel); this is where
// the doctor sees it afterwards and corrects it, and where a transfer approved
// without one gets one. Doctor only, outside the admin's read-only view. The
// staff-facing strings are proposed copy.

const PRECISION_LABEL: Record<TreatmentStartPrecision, string> = {
  exact: "Exakt datum",
  estimated: "Uppskattat",
};

const ERROR_TEXT: Record<string, string> = {
  transfer_start_unavailable:
    "Behandlingsstarten kan bara anges för ett godkänt receptövertag.",
  transfer_start_in_future: "Behandlingsstarten kan inte vara senare än i dag.",
};

function formatDay(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function todayInStockholm(): string {
  // sv-SE formats as YYYY-MM-DD.
  return new Date().toLocaleDateString("sv-SE", {
    timeZone: "Europe/Stockholm",
  });
}

// Why she has no day count, in the doctor's words.
function noDayReason(view: TransferTreatmentStartView): string {
  if (!view.startedOn) {
    return "Ingen dagräkning: startdatum saknas. Ange det nedan.";
  }
  if (view.clinicalPhase !== "active_treatment") {
    return "Ingen daglig dagräkning: patienten är i viktstabilisering och följer underhållsresan.";
  }
  return "Dagräkningen börjar när patienten har aktiverat sitt abonnemang.";
}

export function TransferTreatmentStartPanel({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized: () => void;
}) {
  const { claims } = useAuth();
  const readOnly = useReadOnly();
  const isDoctor = claims?.role === "doctor" && !readOnly;
  const [view, setView] = useState<TransferTreatmentStartView | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [startedOn, setStartedOn] = useState("");
  const [precision, setPrecision] = useState<TreatmentStartPrecision | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  const load = useCallback(async () => {
    try {
      setView(await fetchTransferTreatmentStart(patientId));
      setLoadError(false);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorizedRef.current();
        return;
      }
      setLoadError(true);
    }
  }, [patientId]);

  useEffect(() => {
    if (isDoctor) void load();
  }, [isDoctor, load]);

  if (!isDoctor) return null;
  if (loadError) {
    return (
      <p className="text-xs text-textSecondary">
        Behandlingsstarten kunde inte läsas. Ladda om sidan.
      </p>
    );
  }
  if (!view || !view.applicable) return null;

  function open() {
    setEditing(true);
    setSaved(false);
    setError(null);
    setStartedOn(view?.startedOn ?? "");
    setPrecision(view?.precision ?? null);
  }

  async function save() {
    if (!startedOn || !precision) return;
    setBusy(true);
    setError(null);
    try {
      setView(
        await setTransferTreatmentStart(patientId, { startedOn, precision }),
      );
      setEditing(false);
      setSaved(true);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      const code = e instanceof Error ? e.message : "";
      setError(ERROR_TEXT[code] ?? "Ändringen kunde inte sparas. Försök igen.");
    } finally {
      setBusy(false);
    }
  }

  const today = todayInStockholm();

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm text-sm">
      <h2 className="font-display text-lg">Behandlingsstart (receptövertag)</h2>
      <dl className="grid grid-cols-[auto_1fr] gap-x-md gap-y-xs">
        <dt className="text-textSecondary">Behandlingsstart</dt>
        <dd>
          {view.startedOn ? formatDay(view.startedOn) : "Saknas"}
          {view.precision ? (
            <span
              className={
                view.precision === "estimated"
                  ? "ml-sm rounded-sm bg-surfaceMuted px-xs text-xs"
                  : "ml-sm text-xs text-textSecondary"
              }
            >
              {PRECISION_LABEL[view.precision]}
            </span>
          ) : null}
        </dd>
        {view.treatmentDay !== null ? (
          <>
            <dt className="text-textSecondary">Behandlingsdag i dag</dt>
            <dd>{`Dag ${view.treatmentDay}`}</dd>
            <dt className="text-textSecondary">Dagens tips</dt>
            <dd>
              {view.dtDay !== null
                ? `DT-${String(view.dtDay).padStart(2, "0")}`
                : "Inget dagligt tips från dag 61 (veckovis yta)"}
            </dd>
          </>
        ) : null}
        {view.joinedOn ? (
          <>
            <dt className="text-textSecondary">Aktiv hos Ella sedan</dt>
            <dd>
              {formatDay(view.joinedOn)}
              {view.firstDtDay !== null && view.firstDtDay <= 60
                ? ` (första tips DT-${String(view.firstDtDay).padStart(2, "0")})`
                : ""}
            </dd>
          </>
        ) : null}
      </dl>

      {view.treatmentDay === null ? (
        <p className="text-xs text-textSecondary">{noDayReason(view)}</p>
      ) : null}
      {saved ? (
        <p className="text-xs text-textSecondary" role="status">
          Behandlingsstarten är sparad. Dagräkningen och dagens tips följer det
          nya datumet från i dag.
        </p>
      ) : null}

      {editing ? (
        <div className="flex flex-col gap-sm rounded-md border border-surfaceMuted p-sm">
          <label className="flex flex-col gap-xs">
            <span>
              Startdatum för nuvarande sammanhängande behandlingsperiod
            </span>
            <input
              type="date"
              value={startedOn}
              max={today}
              onChange={(e) => setStartedOn(e.target.value)}
              disabled={busy}
              className="self-start rounded-md border border-surfaceMuted px-sm py-xs"
            />
          </label>
          <fieldset className="flex flex-wrap gap-md">
            <legend className="mb-xs text-textSecondary">Precision</legend>
            {(["exact", "estimated"] as const).map((p) => (
              <label key={p} className="flex items-center gap-xs">
                <input
                  type="radio"
                  name={`transfer-start-precision-${patientId}`}
                  checked={precision === p}
                  onChange={() => setPrecision(p)}
                  disabled={busy}
                />
                {PRECISION_LABEL[p]}
              </label>
            ))}
          </fieldset>
          <p className="text-xs text-textSecondary">
            Startdatumet räknas som behandlingsdag 1 och styr vilket dagligt
            tips patienten får. Tidigare tips visas inte i efterhand.
          </p>
          <div className="flex flex-wrap items-center gap-sm">
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || !startedOn || !precision || startedOn > today}
              className="rounded-md bg-brand px-md py-xs text-sm text-white hover:opacity-90 disabled:opacity-50"
            >
              Spara behandlingsstart
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={busy}
              className="text-sm text-textSecondary underline"
            >
              Avbryt
            </button>
          </div>
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
      ) : (
        <div className="flex flex-wrap gap-sm">
          <button
            type="button"
            onClick={open}
            className="rounded-md border border-surfaceMuted px-md py-xs text-sm text-textPrimary hover:border-brand"
          >
            {view.startedOn
              ? "Korrigera behandlingsstart"
              : "Ange behandlingsstart"}
          </button>
        </div>
      )}
    </section>
  );
}
